import React from 'react';
import { reloadIfSiteUpdated } from './siteVersion';

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fichier en échec : on le télécharge de nouveau depuis le serveur, sans le cache du navigateur.
 * Une réponse « introuvable » gardée en cache (par exemple pendant une ancienne mise à jour)
 * est ainsi remplacée par le vrai fichier avant le nouvel essai.
 */
export async function refreshFailedChunk(error) {
  const url = error?.request;
  if (!url || typeof fetch !== 'function') return;
  try {
    await fetch(url, { cache: 'reload', credentials: 'same-origin' });
  } catch {
    // pas de réseau : le nouvel essai échouera aussi, puis l'écran « Recharger la page » s'affichera
  }
}

/**
 * Charge un fichier de page en réessayant si le téléchargement échoue.
 * - Site mis à jour pendant la visite (fichiers renommés) : rechargement immédiat de la page, une fois ;
 * - réponse « introuvable » restée en cache : fichier téléchargé de nouveau avant chaque essai ;
 * - coupure réseau de quelques secondes (4G instable, ordinateur qui sort de veille) : nouveaux essais.
 * @param {() => Promise<any>} factory par exemple () => import('../pages/Projects')
 * @param {number[]} delays attente avant chaque nouvel essai, en millisecondes
 */
export async function importWithRetry(factory, delays = [1000, 2500, 5000]) {
  for (const [index, delay] of delays.entries()) {
    try {
      return await factory();
    } catch (error) {
      // Dès le premier échec : si le site a été mis à jour, recharger plutôt qu'attendre
      if (index === 0 && await reloadIfSiteUpdated()) {
        return new Promise(() => {}); // la page se recharge
      }
      await refreshFailedChunk(error);
      await wait(delay);
    }
  }
  // Dernier essai : son échec est géré par ChunkErrorBoundary (écran « Recharger la page »)
  return factory();
}

/** Équivalent de React.lazy, avec nouveaux essais automatiques. */
export const lazyWithRetry = (factory, delays) => React.lazy(() => importWithRetry(factory, delays));

export default lazyWithRetry;
